/**
 * KPI Formatting Utilities for WaveScoop Dashboard
 * Provides consistent formatting for KPI values, impressions, engagement, and reputation points
 */

/**
 * Format large numbers with appropriate abbreviations (K, M, B)
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted number with abbreviation
 */
export function formatLargeNumber(value, decimals = 1) {
  if (value === 0) return '0';
  if (value === null || value === undefined) return '--';
  
  const num = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  if (num >= 1000000000) {
    return `${sign}${(num / 1000000000).toFixed(decimals)}B`;
  }
  if (num >= 1000000) {
    return `${sign}${(num / 1000000).toFixed(decimals)}M`;
  }
  if (num >= 1000) {
    return `${sign}${(num / 1000).toFixed(decimals)}K`;
  }
  
  return `${sign}${num.toLocaleString()}`;
}

/**
 * Format impression values with appropriate units
 * @param {number} impressions - The impressions value
 * @returns {string} Formatted impressions with unit
 */
export function formatImpressions(impressions) {
  const formatted = formatLargeNumber(impressions);
  return `${formatted} impressions`;
}

/**
 * Format engagement values with appropriate units  
 * @param {number} engagement - The engagement value
 * @returns {string} Formatted engagement with unit
 */
export function formatEngagement(engagement) {
  const formatted = formatLargeNumber(engagement);
  return `${formatted} interactions`;
}

/**
 * Format reputation points with appropriate sign and unit
 * @param {number} repPoints - The reputation points value
 * @returns {string} Formatted reputation points with sign and unit
 */
export function formatReputationPoints(repPoints) {
  if (repPoints === 0) return '0 points';
  if (repPoints === null || repPoints === undefined) return '-- points';
  
  const sign = repPoints > 0 ? '+' : '';
  const rounded = Math.round(repPoints * 100) / 100; // Round to 2 decimal places, then remove trailing zeros
  return `${sign}${rounded} points`;
}

/**
 * Format percentage values with appropriate precision
 * @param {number} percentage - The percentage value (0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage
 */
export function formatPercentage(percentage, decimals = 1) {
  if (percentage === null || percentage === undefined) return '--%';
  return `${percentage.toFixed(decimals)}%`;
}

/**
 * Format hit rate as percentage with appropriate styling
 * @param {number} hitRate - Hit rate as decimal (0-1) or percentage (0-100)
 * @returns {string} Formatted hit rate percentage
 */
export function formatHitRate(hitRate) {
  if (hitRate === null || hitRate === undefined) return '--%';
  
  // Handle both decimal (0-1) and percentage (0-100) formats
  const percentage = hitRate <= 1 ? hitRate * 100 : hitRate;
  return `${percentage.toFixed(0)}%`;
}

/**
 * Format KPI card titles with consistent capitalization
 * @param {string} title - The raw title string
 * @returns {string} Properly formatted title
 */
export function formatKPITitle(title) {
  return title
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format delta values for net impact display
 * @param {number} value - The delta value
 * @param {string} unit - The unit type ('impressions', 'engagement', 'points')
 * @returns {string} Formatted delta with appropriate sign and unit
 */
export function formatDelta(value, unit) {
  if (value === 0) return `±0 ${unit}`;
  if (value === null || value === undefined) return `-- ${unit}`;
  
  const sign = value > 0 ? '+' : '';
  let formattedValue;
  
  switch (unit) {
    case 'impressions':
      formattedValue = formatLargeNumber(value);
      return `${sign}${formattedValue} impressions`;
    case 'engagement':
      formattedValue = formatLargeNumber(value);
      return `${sign}${formattedValue} interactions`;
    case 'points':
      return `${sign}${value} reputation points`;
    default:
      formattedValue = formatLargeNumber(value);
      return `${sign}${formattedValue} ${unit}`;
  }
}

/**
 * Get color variant based on KPI value (for Material-UI theme colors)
 * @param {number} value - The KPI value
 * @param {string} type - The KPI type ('prevented', 'achieved', 'net')
 * @returns {string} Material-UI color variant
 */
export function getKPIColor(value, type) {
  if (value === null || value === undefined) return 'info';
  
  switch (type) {
    case 'prevented':
      return value > 0 ? 'success' : 'info';
    case 'achieved':
      return value > 0 ? 'primary' : 'info';
    case 'net':
      if (value > 0) return 'success';
      if (value < 0) return 'warning';
      return 'info';
    default:
      return 'primary';
  }
}

/**
 * Format time period for KPI date ranges
 * @param {string} fromDate - ISO date string  
 * @param {string} toDate - ISO date string
 * @returns {string} Formatted date range
 */
export function formatDateRange(fromDate, toDate) {
  try {
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    const options = { 
      month: 'short', 
      day: 'numeric', 
      year: from.getFullYear() !== to.getFullYear() ? 'numeric' : undefined 
    };
    
    return `${from.toLocaleDateString('en-US', options)} - ${to.toLocaleDateString('en-US', options)}`;
  } catch (error) {
    return 'Date range unavailable';
  }
}

/**
 * Create tooltip content for KPI methodology explanation
 * @param {string} type - KPI type ('backlash-prevented', 'boost-achieved', 'net-impact')
 * @param {Object} data - KPI data for specific context
 * @returns {string} Formatted tooltip content
 */
export function createKPITooltip(type, data) {
  const baseTooltips = {
    'backlash-prevented': 'Negative impact avoided by not publishing risky content. Calculated from posts with repDelta < 0 and negativePct ≥ 50%.',
    'boost-achieved': 'Positive impact generated from published content. Calculated from posts with repDelta > 0 or positivePct ≥ 60%.',
    'net-impact': 'Combined view of your content impact. Net result of positive gains minus avoided negative impact.'
  };
  
  const baseTooltip = baseTooltips[type] || 'KPI calculation based on simulation analysis.';
  
  // Add specific data context if available
  if (data && type === 'backlash-prevented') {
    return `${baseTooltip} (${data.postsAvoided || 0} posts flagged)`;
  }
  
  if (data && type === 'boost-achieved') {
    return `${baseTooltip} (${data.postsCount || 0} posts published)`;
  }
  
  return baseTooltip;
}

// Export default object for backward compatibility
const kpiFormattingUtils = {
  formatLargeNumber,
  formatImpressions,
  formatEngagement,
  formatReputationPoints,
  formatPercentage,
  formatHitRate,
  formatKPITitle,
  formatDelta,
  getKPIColor,
  formatDateRange,
  createKPITooltip
};

export default kpiFormattingUtils;